import React, { useEffect, useRef, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import { Prompt } from 'react-router-dom';
import IcomoonReact from 'icomoon-react';
import { useTheme } from 'emotion-theming';
import styled from '@emotion/styled';

import iconSet from '../../shared/images/teambuildr-selection.json';
import HeaderText from '../../shared/_TBComponents/HeaderText';
import Icon from '../../shared/_TBComponents/Icon';
import SharedTemplatesGrid from '../sports-science/components/presentational/sharedTemplateComponents/SharedTemplatesGrid';
import TemplateSearchBar from '../sports-science/components/presentational/sharedTemplateComponents/TemplateSearchBar';
import NewVBTDashboard from './NewVBTDashboard';
import OverviewComponent from './components/presentational/overviewComponent';
import AnalysisComponent from './components/presentational/analysisComponent';
import { TransitionGlobal } from '../../shared/GlobalStyles';
import { dashboardTracker, onboardingTracker } from '../../shared/utils/amplitudeHelper';
import { getModuleInfoByTitle } from '../sports-science/components/presentational/packages';
import {
  clearVBTData,
  fetchGroups,
  fetchVBTTemplates,
  setAboutVBTDashModal,
  setActiveVBTDash,
  setDeleteVBTDashModal,
  setDuplicateVBTDashModal,
  setEditVBTDashModal,
  setExercises,
  setGraphThresholds,
  setGroup,
  setGroups,
  setIsFilterShowing,
  setIsNewVBTDash,
  setIsVBTDashDisplayed,
  setNextVBTDashLocation,
  setOnboardingModal,
  setShareVBTDashModal,
  setTableThreshold,
  setUnsavedVBTDashModal,
  setVBTDashView,
} from './ducks/vbtDashboardActions';
import OnboardingModal from '../../shared/_TBComponents/OnboardingModal';
import { createViewedFeaturePayload, FEATURES, hasViewedFeature } from '../../shared/utils/onboardingStatus';
import { setViewedFeatures } from '../sports-science/ducks/sportsScienceActions';

const BackButtonContainer = styled('div')`
  cursor: pointer;
  margin-bottom: 4px;
`;

const Container = styled('div')`
  width: 100%;
`;

const DashboardHeader = styled('div')`
  align-items: flex-end;
  background-color: ${(props) => props.bgColor};
  display: flex;
  justify-content: space-between;
  padding-left: 20px;
  padding-top: 30px;
  width: 100%;

  @media print {
    display: none;
  }
`;

const HeaderTextAndArrow = styled('div')`
  align-items: center;
  display: flex;
  padding-bottom: 8px;
`;

const HeaderWidthContainer = styled('div')`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const IconsAndSearch = styled('div')`
  align-items: center;
  display: flex;
`;

const InfoWrapper = styled('div')`
  align-items: center;
  border: 2px solid #9e9e9e;
  border-radius: 50%;
  cursor: ${(props) => (props.disabled ? 'cursor' : 'pointer')};
  display: flex;
  height: 20px;
  justify-content: center;
  margin-left: 10px;
  transition: ${TransitionGlobal};
  width: 20px;
  :hover {
    opacity: ${(props) => (props.disabled ? '1' : '.7')};
  }
  @media only screen and (max-width: 601px) {
    display: none;
  }
`;

const MenuContainer = styled('div')`
  display: flex;
`;

const MenuItem = styled('button')`
  border: none;
  border-bottom: ${(props) => (props.isActive ? '3px solid #1787FB!important' : '3px solid white')};
  color: ${(props) => (!props.isActive ? '#9E9E9E' : '#444444')};
  font-family: "Nunito Sans";
  font-size: 16px;
  font-weight: 400;
  margin-left: 10px;
  margin-right: 12px;
`;

const PageWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
`;

const VBTDashboard = () => {
  const activeTemplate = useSelector((state) => state.vbtDashboard.data.activeTemplate);
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const isDashDisplayed = useSelector((state) => state.vbtDashboard.ui.isDashDisplayed);
  const isNewVBTDash = useSelector((state) => state.vbtDashboard.ui.isNewVBTDash);
  const nextLocation = useSelector((state) => state.vbtDashboard.data.nextLocation);
  const onboardingModalShowing = useSelector(
    (state) => state.vbtDashboard.ui.onboardingModalShowing,
  );
  const vbtTemplates = useSelector((state) => state.vbtDashboard.data.templates);
  const vbtDashView = useSelector((state) => state.vbtDashboard.ui.vbtDashView);
  const viewedFeatures = useSelector((state) => state.sportsScience.data.viewedFeatures);
  const viewedFeaturesLoading = useSelector(
    (state) => state.sportsScience.ui.viewedFeaturesLoading,
  );

  const dispatch = useDispatch();
  const pageRef = useRef(null);
  const theme = useTheme();
  const searchInputRef = useRef(null);

  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const moduleInfo = getModuleInfoByTitle('VBT Dashboard');
  const unsavedDash = isDashDisplayed && !activeTemplate;

  const { content } = moduleInfo;

  // Resets dashboard on initial load
  // ex. user navigates away to another dash and then back, we should show entry screen
  useEffect(() => {
    dispatch(clearVBTData());
  }, []);

  const handleContainerClick = (e) => {
    if (
      e.target.id !== 'vbt-dash-search-container'
      && e.target.id !== 'vbt-dash-search-icon-container'
      && e.target.id !== 'vbt-dash-search-container-input'
      && e.target.id !== 'vbt-dash-mag-icon'
      && isSearchFocused
      && searchTerm.length === 0
    ) {
      setIsSearchFocused(false);
      if (searchInputRef?.current) {
        searchInputRef?.current?.blur();
      }
    }
  };

  const handleFocusSearch = () => {
    setIsSearchFocused(true);
    setTimeout(() => {
      if (searchInputRef?.current) {
        searchInputRef?.current?.focus();
      }
    }, 0);
  };

  const handleClose = () => {
    const viewedFeaturePayload = createViewedFeaturePayload(
      currentUser?.accountCode,
      currentUser?.id,
      FEATURES.VBT_DASHBOARD,
    );
    batch(() => {
      dispatch(setOnboardingModal(false));
      if (!hasViewedFeature(viewedFeatures, FEATURES.VBT_DASHBOARD)) {
        dispatch(setViewedFeatures(viewedFeaturePayload));
      }
    });
  };

  const handleCreateNewDashboard = () => {
    // Amplitude tracking
    dashboardTracker(
      'VBT Dashboard',
      'Main Screen',
      'Create new dashboard clicked',
    );
    batch(() => {
      dispatch(setGroup(null));
      dispatch(setIsNewVBTDash(true));
    });
  };

  const handleMenuClick = (view) => {
    if (view === vbtDashView) return;

    batch(() => {
      dispatch(setVBTDashView(view));
    });

    // Amplitude tracking
    dashboardTracker(
      'VBT Dashboard',
      view === 'analysis' ? 'Group Analysis' : 'Performance Overview',
      'Menu item clicked',
    );
  };

  // Handles if a user tries to navigate to another react page
  const handleNavigation = (location) => {
    if (unsavedDash && !nextLocation) {
      batch(() => {
        dispatch(setNextVBTDashLocation(location.pathname));
        dispatch(setUnsavedVBTDashModal(true));
      });
      return false;
    }
    return true;
  };

  const handleOnboardingModal = () => {
    dispatch(setOnboardingModal(true));
  };

  const handleReturnClick = () => {
    // Amplitude tracking
    dashboardTracker('VBT Dashboard', 'Main Screen', 'Return to main screen clicked');
    batch(() => {
      dispatch(setIsFilterShowing(false));
      if (isNewVBTDash) {
        dispatch(setIsNewVBTDash(false));
      } else if (unsavedDash) {
        dispatch(setIsVBTDashDisplayed(false));
        dispatch(setIsNewVBTDash(true));
      } else {
        dispatch(setIsVBTDashDisplayed(false));
      }
    });
  };

  const handleTemplateView = (template) => {
    // Amplitude tracking
    dashboardTracker(
      'VBT Dashboard',
      'Main Screen',
      'Saved dashboard opened',
    );
    const isOwner = currentUser?.id === template?.createdBy;

    batch(() => {
      dispatch(setActiveVBTDash(template));
      dispatch(setExercises(template.exercises));

      if (template.thresholds?.graphThresholds?.length > 0) {
        dispatch(setGraphThresholds(template.thresholds.graphThresholds));
      }

      if (template.groups?.length > 0 || !isOwner) {
        dispatch(setGroups(template.groups));
      }

      dispatch(setIsVBTDashDisplayed(true));

      if (template.thresholds?.tableThreshold?.max) {
        dispatch(setTableThreshold(template.thresholds.tableThreshold));
      }
    });
  };

  const setIsVBTOverviewTrue = () => {
    dispatch(setIsVBTDashDisplayed(true));
  };

  const templateActions = {
    handleCreateNewDashboard,
    handleTemplateView,
    setDeleteModal: setDeleteVBTDashModal,
    setAboutModal: setAboutVBTDashModal,
    setEditModal: setEditVBTDashModal,
    setDuplicateModal: setDuplicateVBTDashModal,
    setShareModal: setShareVBTDashModal,
    clearActiveTemplate: clearVBTData,
  };

  useEffect(() => {
    if (!viewedFeaturesLoading && !hasViewedFeature(viewedFeatures, FEATURES.VBT_DASHBOARD)) {
      // Amplitude tracking
      onboardingTracker(
        'Modal Opened',
        'VBT',
        'Onboarding modal opened on user\'s first view of dashboard',
      );
      dispatch(setOnboardingModal(true));
    }
  }, [viewedFeaturesLoading, viewedFeatures]);

  useEffect(() => {
    if (!isDashDisplayed && currentUser) {
      dispatch(fetchGroups(currentUser.accountCode));
    }
  }, [isDashDisplayed, currentUser]);

  useEffect(() => {
    if (Object.keys(currentUser).length) {
      dispatch(fetchVBTTemplates(currentUser.accountCode));
    }
  }, [currentUser]);

  useEffect(() => {
    dispatch(setIsFilterShowing(false));
  }, [vbtDashView]);

  // Handles if a user tries to leave the site, or navigate to PHP page, with an unsaved dashboard
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      event.preventDefault();

      // Note, custom messages do not work for Chrome
      const customMessage = `This dashboard has not been saved, are you sure you want to exit without saving?
        Exiting now will discard any unsaved progress. If you have made any updates,
        close this modal to review and save your changes.`;

      return customMessage;
    };

    if (unsavedDash) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [unsavedDash]);

  return (
    <Container
      onClick={handleContainerClick}
    >
      <Helmet>
        <title>VBT Dashboard | TeamBuildr</title>
      </Helmet>
      <PageWrapper ref={pageRef}>
        <DashboardHeader bgColor={theme.colors.background[0]}>
          <HeaderWidthContainer>
            <HeaderTextAndArrow>
              {isDashDisplayed || isNewVBTDash ? (
                <BackButtonContainer>
                  <IcomoonReact
                    iconSet={iconSet}
                    size={20}
                    icon='left-arrow'
                    onClick={handleReturnClick}
                  />
                </BackButtonContainer>
              ) : null}
              <HeaderText
                fontWeight='300'
                letterSpacing='normal'
                style={{
                  marginLeft: 10,
                }}
              >
                VBT Dashboard
              </HeaderText>
              <InfoWrapper onClick={handleOnboardingModal}>
                <Icon
                  icon='info'
                  size='12px'
                  styledProps={{
                    display: 'flex',
                  }}
                />
              </InfoWrapper>
            </HeaderTextAndArrow>
            {!isDashDisplayed && !isNewVBTDash && (
              <MenuContainer>
                <IconsAndSearch>
                  <MenuItem>
                    <TemplateSearchBar
                      handleClick={handleFocusSearch}
                      iconContainerId='vbt-dash-search-icon-container'
                      iconId='vbt-dash-mag-icon'
                      id='vbt-dash-search-container'
                      isFocused={isSearchFocused}
                      searchId='vbt-dash-search-container-input'
                      searchRef={searchInputRef}
                      searchTerm={searchTerm}
                      setSearchTerm={setSearchTerm}
                    />
                  </MenuItem>
                </IconsAndSearch>
              </MenuContainer>
            )}
            {isDashDisplayed && !isNewVBTDash && (
              <MenuContainer>
                <MenuItem
                  isActive={vbtDashView === 'overview'}
                  onClick={() => handleMenuClick('overview')}
                >
                  Performance Overview
                </MenuItem>
                <MenuItem
                  isActive={vbtDashView === 'analysis'}
                  onClick={() => handleMenuClick('analysis')}
                >
                  Group Analysis
                </MenuItem>
              </MenuContainer>
            )}
          </HeaderWidthContainer>
        </DashboardHeader>
        {!isDashDisplayed && !isNewVBTDash && (
          <SharedTemplatesGrid
            actions={templateActions}
            dashboard='VBT'
            legacyName='VBT'
            searchTerm={searchTerm}
            templates={vbtTemplates}
          />
        )}
        {isDashDisplayed && vbtDashView === 'overview' && (
          <OverviewComponent />
        )}
        {isDashDisplayed && vbtDashView === 'analysis' && (
          <AnalysisComponent />
        )}
        {!isDashDisplayed && isNewVBTDash && (
          <NewVBTDashboard
            setIsOverview={setIsVBTOverviewTrue}
            setNewDash={setIsNewVBTDash}
          />
        )}
      </PageWrapper>
      <OnboardingModal
        badgeText='GET STARTED'
        content={content}
        dashName='VBT'
        isActive={onboardingModalShowing}
        isUpgrade
        maxHeight={null}
        minHeight={null}
        onClose={handleClose}
        onPrimaryAction={handleClose}
        primaryButtonText='GET STARTED'
        showSingleButton
      />
      <Prompt when={unsavedDash} message={handleNavigation} />
    </Container>
  );
};

export default VBTDashboard;
